import { ApiError } from '@lib/network/api';
import { computeTopUpDetails, handleCreditsPurchase } from '@lib/purchase';
import { showToast } from '@lib/utils/toastmaster';
import { formatCredits } from '@lib/utils/units';
import { billingStore, SECONDS_IN_HOUR } from '@stores/Billing';
import { trackingStore } from '@stores/Tracking';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseTopUpParams {
  creditCost: number;
  currentCredits: number;
  insufficient: boolean;
  source: string;
  storage?: {
    afterProcessing: number;
    duration: number;
  };
}

export function useTopUp({
  creditCost,
  currentCredits,
  insufficient,
  source,
  storage,
}: UseTopUpParams) {
  const navigate = useNavigate();

  // ── Credit top-up details ────────────────────────────────────────────
  const details = useMemo(() => {
    if (!insufficient) return null;
    // computeTopUpDetails returns null when cost == available (deficit is 0).
    // Since insufficient is true (creditsAfter <= 0), fall back to minimum 1-pack top-up.
    return computeTopUpDetails(creditCost, currentCredits) ?? computeTopUpDetails(1, 0);
  }, [insufficient, creditCost, currentCredits]);

  // ── Storage plan check ───────────────────────────────────────────────
  const storageInsufficient = storage ? storage.afterProcessing <= 0 : false;

  // Warm the subscription cache eagerly so it's ready when needed
  useEffect(() => {
    if (storage) {
      billingStore.ensureSubscriptionsLoaded();
    }
  }, [!!storage]);

  const canAnyPlanCoverStorage = useMemo(() => {
    if (!storageInsufficient || !storage) return false;
    // Uses storageUsageSeconds (not data.usage directly) so debug overrides propagate here.
    const totalStorageNeeded = billingStore.storageUsageSeconds + storage.duration;
    return billingStore.availableSubscriptions.some(
      (sub) => sub.quota.storageHours * SECONDS_IN_HOUR >= totalStorageNeeded,
    );
  }, [storageInsufficient, storage, billingStore.availableSubscriptions]);

  // Storage insufficient always takes priority over credits: credits can be topped up
  // freely, but storage is plan-bound — adding credits won't help if storage is full.
  const isStoragePriority = storageInsufficient;

  // ── Action handler ───────────────────────────────────────────────────
  const [isToppingUp, setIsToppingUp] = useState(false);

  const handleTopUp = useCallback(async () => {
    if (isStoragePriority) {
      if (canAnyPlanCoverStorage) {
        navigate('/pricing');
      } else {
        trackingStore.contactSalesClicked({ source });
        window.location.href = 'mailto:sales@starzero.ai';
      }
      return;
    }
    if (!details || isToppingUp) return;
    setIsToppingUp(true);
    try {
      if (details.shouldContactSales) {
        trackingStore.contactSalesClicked({ source });
        window.location.href = 'mailto:sales@starzero.ai';
      } else {
        await handleCreditsPurchase(details.quantity);
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        showToast('topup-rate-limited');
      }
    } finally {
      setIsToppingUp(false);
    }
  }, [isStoragePriority, canAnyPlanCoverStorage, navigate, details, source, isToppingUp]);

  // ── Labels ───────────────────────────────────────────────────────────
  // Full inline label (for ImpactPopup's <a> tag)
  const label = details
    ? details.shouldContactSales
      ? 'Contact sales'
      : `Top-up ${formatCredits(details.topUpCreditsApi)} credits for $${details.topUpPrice}`
    : null;

  // Short button label
  const buttonLabel = (() => {
    if (isStoragePriority) {
      return canAnyPlanCoverStorage ? 'Upgrade' : 'Contact sales';
    }
    if (!insufficient) return null;
    if (details?.shouldContactSales) return 'Contact sales';
    return 'Add credits';
  })();

  // Tooltip for buttons
  const tooltip = (() => {
    if (isStoragePriority) {
      return canAnyPlanCoverStorage
        ? 'Upgrade your plan to get more storage'
        : 'Contact sales for more storage';
    }
    if (!insufficient || !details) return null;
    if (details.shouldContactSales) return 'Contact sales for more credits';
    return `Top-up ${formatCredits(details.topUpCreditsApi)} credits for $${details.topUpPrice}`;
  })();

  const needsAction = insufficient || isStoragePriority;

  return {
    details,
    handleTopUp,
    isToppingUp,
    label,
    buttonLabel,
    tooltip,
    isStoragePriority,
    needsAction,
  };
}
